import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { RefreshCw, Wallet, AlertTriangle, CheckCircle2, TrendingUp, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtCredits = (v) => `${Number(v || 0).toFixed(2)} cr.`;
const fmtUSD = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtEUR = (v) => `€${Number(v || 0).toFixed(2)}`;

/**
 * Banner compacto de saldo IA — se muestra en Automatización y Pagos de Tasas.
 * Auto-refresca cada 60s.
 *
 * Props:
 *  - compact (bool): versión mini, una sola línea (default: false)
 *  - onResetSaldo (fn): callback tras reset (para refrescar padres)
 */
export default function PanelConsumoIA({ compact = false, onResetSaldo }) {
  const navigate = useNavigate();
  const [saldo, setSaldo] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/ia-consumo/saldo`),
        axios.get(`${API}/ia-consumo/resumen`),
      ]);
      setSaldo(s.data);
      setResumen(r.data);
    } catch (e) {
      console.error('Error cargando consumo IA', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
    const iv = setInterval(cargar, 60_000);
    return () => clearInterval(iv);
  }, [cargar]);

  const handleReset = async () => {
    if (!window.confirm('¿Confirmas que acabas de recargar el saldo del Emergent LLM Key? Esto resetea el contador del saldo restante desde ahora.')) return;
    setResetting(true);
    try {
      await axios.post(`${API}/ia-consumo/reset-saldo`);
      toast.success('Saldo sincronizado tras recarga');
      await cargar();
      if (onResetSaldo) onResetSaldo();
    } catch (e) {
      toast.error('Error al resetear saldo');
    } finally {
      setResetting(false);
    }
  };

  if (loading || !saldo) {
    return (
      <Card className="border-dashed" data-testid="panel-consumo-ia-loading">
        <CardContent className="py-3 text-sm text-slate-500">Cargando consumo IA…</CardContent>
      </Card>
    );
  }

  const colorPorEstado = {
    ok: { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle2 },
    warning: { bar: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle },
    critical: { bar: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: AlertTriangle },
  };
  const colors = colorPorEstado[saldo.estado] || colorPorEstado.ok;
  const IconEstado = colors.icon;

  if (compact) {
    return (
      <div
        onClick={() => navigate('/configuracion/consumo-ia')}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${colors.bg} ${colors.border} cursor-pointer hover:shadow-md hover:brightness-95 transition-all`}
        title="Ver detalle del consumo IA y configuración"
        data-testid="panel-consumo-ia-compact"
      >
        <IconEstado className={`w-4 h-4 ${colors.text}`} />
        <div className={`text-sm font-semibold ${colors.text}`} data-testid="panel-consumo-ia-saldo-compact">
          Saldo IA: {fmtCredits(saldo.restante_credits)}
          <span className="text-slate-500 font-normal ml-2">
            ({fmtUSD(saldo.restante_usd)} · {fmtEUR(saldo.restante_eur)})
          </span>
        </div>
        <Progress value={saldo.porcentaje_usado} className="flex-1 h-2 max-w-[200px]" />
        <span className="text-xs text-slate-500">{saldo.porcentaje_usado.toFixed(0)}% usado</span>
        <span className="text-xs text-slate-400 ml-auto">Click para ver detalle →</span>
      </div>
    );
  }

  return (
    <Card className={`${colors.bg} ${colors.border}`} data-testid="panel-consumo-ia">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Wallet className={`w-5 h-5 ${colors.text}`} />
            Consumo IA - Saldo Emergent LLM Key
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={cargar}
              className="h-7 text-xs"
              data-testid="panel-consumo-ia-refresh"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Actualizar
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleReset}
              disabled={resetting}
              className="h-7 text-xs"
              data-testid="panel-consumo-ia-reset"
              title="Marcar que has recargado el saldo del Emergent LLM Key"
            >
              {resetting ? 'Guardando…' : 'He recargado'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Saldo principal */}
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <div>
              <span className={`text-2xl font-bold ${colors.text}`} data-testid="panel-consumo-ia-saldo-usd">
                {fmtCredits(saldo.restante_credits)}
              </span>
              <span className="text-slate-500 ml-2">/ {fmtCredits(saldo.max_budget_credits)}</span>
              <span className="text-sm text-slate-500 ml-3">
                ≈ {fmtUSD(saldo.restante_usd)} · {fmtEUR(saldo.restante_eur)}
              </span>
            </div>
            <Badge
              variant="outline"
              className={`${colors.text} ${colors.border}`}
              data-testid="panel-consumo-ia-estado-badge"
            >
              {saldo.estado === 'ok' ? 'Saldo OK' : saldo.estado === 'warning' ? 'Saldo bajo' : 'Saldo crítico'}
            </Badge>
          </div>
          <Progress value={saldo.porcentaje_usado} className="h-3" />
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>
              Gastado: {fmtCredits(saldo.gastado_credits)}
              <span className="text-slate-400"> ({fmtUSD(saldo.gastado_usd)} · {fmtEUR(saldo.gastado_eur)})</span>
            </span>
            <span>{saldo.porcentaje_usado.toFixed(1)}% usado · {saldo.llamadas_desde_recarga} llamadas</span>
          </div>
        </div>

        {/* Alerta saldo bajo */}
        {saldo.estado !== 'ok' && (
          <div className={`flex items-start gap-2 text-sm ${colors.text} border-t pt-2`}>
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              {saldo.estado === 'critical'
                ? 'Saldo crítico: recarga ya en Emergent → Profile → Universal Key, o cambia a un modelo más barato (p.ej. gpt-5.4-mini).'
                : 'Saldo bajo: considera recargar antes del próximo análisis masivo.'}
            </div>
          </div>
        )}

        {/* Consumos históricos */}
        {resumen && (
          <div className="grid grid-cols-3 gap-3 pt-2 border-t text-xs">
            <div>
              <div className="text-slate-500">Últimos 7 días</div>
              <div className="font-semibold" data-testid="panel-consumo-ia-7d">
                {fmtUSD(resumen.ultimos_7_dias.usd)} ({fmtEUR(resumen.ultimos_7_dias.eur)})
              </div>
              <div className="text-slate-400">{resumen.ultimos_7_dias.llamadas} llamadas</div>
            </div>
            <div>
              <div className="text-slate-500">Últimos 30 días</div>
              <div className="font-semibold" data-testid="panel-consumo-ia-30d">
                {fmtUSD(resumen.ultimos_30_dias.usd)} ({fmtEUR(resumen.ultimos_30_dias.eur)})
              </div>
              <div className="text-slate-400">{resumen.ultimos_30_dias.llamadas} llamadas</div>
            </div>
            <div>
              <div className="text-slate-500">Histórico total</div>
              <div className="font-semibold" data-testid="panel-consumo-ia-total">
                {fmtUSD(resumen.total_emergent.usd)} ({fmtEUR(resumen.total_emergent.eur)})
              </div>
              <div className="text-slate-400">{resumen.total_emergent.llamadas} llamadas</div>
            </div>
          </div>
        )}

        {/* Footer info */}
        <div className="flex items-center gap-1 text-[11px] text-slate-400 pt-1">
          <Info className="w-3 h-3" />
          <span>
            1 crédito Emergent = ${saldo.credit_rate_usd?.toFixed(2) || '0.20'} · USD→EUR {saldo.usd_eur_rate.toFixed(4)} · Estimación ±15-25%.
            Saldo real en Emergent → Profile → Universal Key.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
